import * as R from 'remeda';
import { useSelector } from 'react-redux';
import { AnnotationListTable } from '@rossum/api-client/shared';
import { GridColumnVisibilityModel } from '@rossum/ui/x-data-grid-pro';
import { userDashboardCustomizationSelector } from './selectors';
import { MetaColDef, SchemaColDef } from './TypedGridColDef';
import { MetaColumnBase } from './useMetaColumns';
import { SchemaColumnBase, useSchemaColumns } from './useSchemaColumns';
import { filterMetaColumns, filterSchemaColumns, getColumnName } from './utils';

export type RowTypeBase = SchemaColumnBase & MetaColumnBase;

export const useColumns = <RowType extends RowTypeBase>({
  activeQueueId,
  tableColumns,
  isTableConfigLoading,
  statusColumns,
  metaColumns,
}: {
  statusColumns: MetaColDef<RowType>[];
  isTableConfigLoading: boolean;
  tableColumns: AnnotationListTable['columns'];
  activeQueueId: number | null;
  metaColumns: MetaColDef<RowType>[];
}): {
  columnsAreReady: boolean;
  columns: Array<SchemaColDef<RowType> | MetaColDef<RowType>>;
  columnVisibilityModel: GridColumnVisibilityModel;
} => {
  const existingSchemaColumns = filterSchemaColumns(tableColumns);

  const userDefinedWidth = useSelector(userDashboardCustomizationSelector);

  const { columns: schemaColumns, isLoading: schemaFieldsLoading } =
    useSchemaColumns<RowType>({
      existingSchemaColumns,
      activeQueueId,
    });

  const savedMetaColumns = filterMetaColumns(tableColumns);

  const mergedMetaColumns = [...metaColumns, ...statusColumns].map(col => {
    const savedColumn = savedMetaColumns.find(c => c.metaName === col.metaName);
    return savedColumn ? { ...col, ...savedColumn } : col;
  });

  const mergedColumns = R.uniqueBy(
    [...mergedMetaColumns, ...schemaColumns],
    col => col.field
  );

  const columnsOrder = tableColumns.map(getColumnName);

  const columns = mergedColumns.map(c => {
    const key = c.field;

    const userWidth = activeQueueId
      ? userDefinedWidth.queues?.width[activeQueueId]?.[key]
      : userDefinedWidth.organization?.width[key];

    return {
      ...c,
      width: userWidth ?? c.width,
    };
  });

  const sortedColumns = R.sortBy(columns, col => {
    const index = columnsOrder.findIndex(
      colName => colName === getColumnName(col)
    );
    return index > -1 ? index : columns.length;
  });

  return {
    // If there are 0 columns, then schema will never start loading (no need)
    columnsAreReady: !isTableConfigLoading && !schemaFieldsLoading,
    columns: sortedColumns,
    columnVisibilityModel: R.fromEntries(
      columns.map(col => [col.field, col.visible])
    ),
  };
};
